import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { FaMapMarkerAlt, FaPhone, FaEnvelope, FaGlobe, FaRupeeSign, FaGraduationCap, FaCalendar } from 'react-icons/fa';
import apiService from '../services/api';
import Loading from '../components/Loading';

const CollegeDetailsPage = () => {
  const { slug } = useParams();
  const [college, setCollege] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCollegeDetails();
  }, [slug]);

  const fetchCollegeDetails = async () => {
    setLoading(true);
    try {
      const response = await apiService.getCollegeDetails(slug);
      if (response.data.success) {
        setCollege(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching college details:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <Loading message="Loading college details..." />;
  if (!college) return <div className="container-custom py-20 text-center">College not found</div>;

  return (
    <>
      <Helmet>
        <title>{college.name} - EduConsult</title>
        <meta name="description" content={college.description || `Learn about ${college.name}, ${college.location}`} />
      </Helmet>

      {/* Hero Banner */}
      <section className="relative h-96 overflow-hidden">
        {college.banner_url ? (
          <img src={college.banner_url} alt={college.name} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-primary-600 to-secondary-600"></div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
        
        <div className="absolute bottom-0 left-0 right-0 pb-8">
          <div className="container-custom">
            <div className="flex items-end gap-6">
              {college.logo_url && (
                <img
                  src={college.logo_url}
                  alt={`${college.name} logo`}
                  className="w-24 h-24 bg-white rounded-xl p-2 shadow-lg"
                />
              )}
              <div className="text-white flex-1">
                <h1 className="text-3xl md:text-4xl font-display font-bold mb-2">{college.name}</h1>
                <div className="flex items-center gap-4 text-sm md:text-base">
                  <span className="flex items-center">
                    <FaMapMarkerAlt className="mr-2" />
                    {college.location}
                  </span>
                  {college.established_year && (
                    <span className="flex items-center">
                      <FaCalendar className="mr-2" />
                      Est. {college.established_year}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Info */}
      <section className="py-8 bg-gray-50 border-b">
        <div className="container-custom">
          <div className="grid md:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="text-2xl font-bold text-primary-600 mb-1">
                {college.college_type ? college.college_type.charAt(0).toUpperCase() + college.college_type.slice(1) : 'N/A'}
              </div>
              <div className="text-sm text-gray-600">College Type</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-primary-600 mb-1">
                {college.accreditation || 'NAAC A+'}
              </div>
              <div className="text-sm text-gray-600">Accreditation</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-primary-600 mb-1">
                #{college.ranking || 'N/A'}
              </div>
              <div className="text-sm text-gray-600">National Ranking</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-primary-600 mb-1 flex items-center justify-center">
                <FaRupeeSign className="text-xl" />
                {college.fees_range_min && college.fees_range_max
                  ? `${(college.fees_range_min / 1000).toFixed(0)}K - ${(college.fees_range_max / 1000).toFixed(0)}K`
                  : 'N/A'}
              </div>
              <div className="text-sm text-gray-600">Fee Range (Annual)</div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-8">
              {/* About */}
              <div className="bg-white rounded-xl p-8 shadow-lg">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">About College</h2>
                <div className="prose max-w-none text-gray-600">
                  {college.about_college || college.description || 'Information not available.'}
                </div>
              </div>

              {/* Courses */}
              {college.courses && college.courses.length > 0 && (
                <div className="bg-white rounded-xl p-8 shadow-lg">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">Courses Offered</h2>
                  <div className="grid md:grid-cols-2 gap-4">
                    {college.courses.map((course) => (
                      <div key={course.id} className="border border-gray-200 rounded-lg p-4 hover:border-primary-300 hover:bg-primary-50 transition-all">
                        <div className="flex items-start justify-between mb-2">
                          <h3 className="font-semibold text-gray-900">{course.course_name}</h3>
                          <span className="text-xs bg-primary-100 text-primary-700 px-2 py-1 rounded">
                            {course.degree_type}
                          </span>
                        </div>
                        <div className="text-sm text-gray-600 space-y-1">
                          {course.duration && <div>Duration: {course.duration}</div>}
                          {course.fees && (
                            <div className="flex items-center font-semibold text-gray-700">
                              <FaRupeeSign className="mr-1" />
                              {course.fees.toLocaleString()}/year
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Admission Process */}
              {college.admission_process && (
                <div className="bg-white rounded-xl p-8 shadow-lg">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">Admission Process</h2>
                  <div className="prose max-w-none text-gray-600">
                    {college.admission_process}
                  </div>
                </div>
              )}

              {/* Facilities */}
              {college.facilities && (
                <div className="bg-white rounded-xl p-8 shadow-lg">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">Facilities</h2>
                  <div className="prose max-w-none text-gray-600">
                    {college.facilities}
                  </div>
                </div>
              )}

              {/* Placements */}
              {college.placements_info && (
                <div className="bg-white rounded-xl p-8 shadow-lg">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4">Placements</h2>
                  <div className="prose max-w-none text-gray-600">
                    {college.placements_info}
                  </div>
                </div>
              )}
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Contact Info */}
              <div className="bg-white rounded-xl p-6 shadow-lg sticky top-24">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Contact Information</h3>
                <div className="space-y-3 text-sm">
                  {college.phone && (
                    <div className="flex items-start gap-3">
                      <FaPhone className="text-primary-500 mt-1" />
                      <div>
                        <div className="text-gray-500 text-xs">Phone</div>
                        <div className="text-gray-900">{college.phone}</div>
                      </div>
                    </div>
                  )}
                  {college.email && (
                    <div className="flex items-start gap-3">
                      <FaEnvelope className="text-primary-500 mt-1" />
                      <div>
                        <div className="text-gray-500 text-xs">Email</div>
                        <div className="text-gray-900">{college.email}</div>
                      </div>
                    </div>
                  )}
                  {college.website_url && (
                    <div className="flex items-start gap-3">
                      <FaGlobe className="text-primary-500 mt-1" />
                      <div>
                        <div className="text-gray-500 text-xs">Website</div>
                        <a
                          href={college.website_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary-600 hover:underline"
                        >
                          Visit Website
                        </a>
                      </div>
                    </div>
                  )}
                  {college.address && (
                    <div className="flex items-start gap-3">
                      <FaMapMarkerAlt className="text-primary-500 mt-1" />
                      <div>
                        <div className="text-gray-500 text-xs">Address</div>
                        <div className="text-gray-900">{college.address}</div>
                      </div>
                    </div>
                  )}
                </div>

                <Link to="/register" className="mt-6 btn btn-primary w-full">
                  Apply for Consultation
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default CollegeDetailsPage;
